import type {
    AvailabilityPolicy,
    AvailabilityPolicyDay,
    AvailabilityPolicyHolidayListResponse,
} from "@kontextwerk/control-web-client"

/** Mirrors the KB membership-settings HTTP projection, not OrganizationUser. */
export type DailyBriefingPreferences = {
  enabled: boolean
  include_pdf: boolean
  include_operations: boolean | null
}
export type MembershipSettings = {
  organization_id: string
  membership_id: string
  user_id: string
  display_name: string
  settings: {
    daily_briefing: DailyBriefingPreferences
    availability_policy_id: string | null
    availability_policy: AvailabilityPolicy | null
  }
}
export const invalid = (): never => {
  throw new TypeError("Invalid membership settings contract")
}
export const record = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return invalid()

  return value as Record<string, unknown>
}
const text = (value: unknown): string => (typeof value === "string" ? value : invalid())
const boolean = (value: unknown): boolean => (typeof value === "boolean" ? value : invalid())
export const uuid = (value: unknown): string => {
  if (typeof value !== "string" || !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(value))
    return invalid()

  return value
}
export const holidayYears = (years: readonly number[]): number[] => {
  if (
    !years.length ||
    years.length > 301 ||
    years.some((year) => !Number.isInteger(year) || year < 1900 || year > 2200)
  )
    return invalid()

  return [...new Set(years)].sort((left, right) => left - right)
}
export const holidays = (
  value: unknown,
  years: readonly number[],
): AvailabilityPolicyHolidayListResponse => {
  const input = record(value)

  if (!Array.isArray(input.items) || input.items.length > 366 * years.length) return invalid()

  const seen = new Set<string>()

  return {
    items: input.items.map((value) => {
      const item = record(value)
      const date = text(item.date)
      const name = text(item.name)

      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
        !years.includes(Number(date.slice(0, 4))) ||
        seen.has(date) ||
        !name.trim()
      )
        return invalid()

      const parsed = new Date(`${date}T00:00:00Z`)

      if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date)
        return invalid()

      seen.add(date)

      return { date, name }
    }),
  }
}
export const briefing = (value: unknown): DailyBriefingPreferences => {
  const input = record(value)

  if (
    Object.keys(input).some(
      (key) => !["enabled", "include_pdf", "include_operations"].includes(key),
    )
  )
    return invalid()

  return {
    enabled: boolean(input.enabled),
    include_pdf: boolean(input.include_pdf),
    include_operations:
      input.include_operations === null ? null : boolean(input.include_operations),
  }
}
const policy = (
  value: unknown,
  organizationId: string,
  policyId: string | null,
): AvailabilityPolicy | null => {
  if (value === null) {
    if (policyId !== null) return invalid()

    return null
  }

  const input = record(value)

  if (
    uuid(input.organization_id) !== organizationId ||
    uuid(input.id) !== policyId ||
    !Array.isArray(input.hours) ||
    !Array.isArray(input.closed_dates)
  )
    return invalid()

  const titles = record(input.closed_date_titles)
  const seen = new Set<number>()
  const hours = input.hours.map((value): AvailabilityPolicyDay => {
    const day = record(value)

    if (
      !Number.isInteger(day.weekday) ||
      Number(day.weekday) < 0 ||
      Number(day.weekday) > 6 ||
      seen.has(Number(day.weekday)) ||
      !Array.isArray(day.windows)
    )
      return invalid()

    seen.add(Number(day.weekday))

    if (day.mode !== "OPEN" && day.mode !== "CLOSED" && day.mode !== "TIMEFRAMES") return invalid()

    return {
      weekday: Number(day.weekday),
      mode: day.mode,
      windows: day.windows.map((value) => {
        const window = record(value)
        const start_time = text(window.start_time)
        const end_time = text(window.end_time)

        if (![start_time, end_time].every((time) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)))
          return invalid()

        return { start_time, end_time }
      }),
    }
  })

  return {
    id: policyId!,
    organization_id: organizationId,
    name: text(input.name),
    timezone: input.timezone === null ? null : text(input.timezone),
    exclude_german_nationwide_holidays: boolean(input.exclude_german_nationwide_holidays),
    hours,
    closed_dates: input.closed_dates.map(text),
    closed_date_titles: Object.fromEntries(
      Object.entries(titles).map(([date, name]) => [date, text(name)]),
    ),
  }
}
export const membershipSettings = (value: unknown, organizationId: string): MembershipSettings => {
  const input = record(value)

  if (uuid(input.organization_id) !== organizationId) return invalid()

  const settings = record(input.settings)
  const policyId =
    settings.availability_policy_id === null ? null : uuid(settings.availability_policy_id)

  return {
    organization_id: organizationId,
    membership_id: uuid(input.membership_id),
    user_id: uuid(input.user_id),
    display_name: text(input.display_name),
    settings: {
      daily_briefing: briefing(settings.daily_briefing),
      availability_policy_id: policyId,
      availability_policy: policy(settings.availability_policy, organizationId, policyId),
    },
  }
}
